import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useRouterState } from '@tanstack/react-router';
import { Link } from './Link.tsx';
import { LearnMoreMenu } from './marketing/LearnMoreMenu.tsx';
import { MarketingMobileMenu } from './marketing/MarketingMobileMenu.tsx';
import { useRouteName } from '../hooks/useRoute.ts';
import { useT } from '../i18n.ts';

interface MarketingLayoutProps {
  children: ComponentChildren;
}

// Lean chrome for the marketing site: brand link, top nav, and a single
// <main>. No sidebar, workspace switcher, keyboard shortcuts, or auth state —
// those belong to the product app. Class names mirror the product app's
// header so the shared marketing styles (app.css / marketing.css) apply
// unchanged. On phone-width viewports the inline nav collapses behind a
// hamburger toggle (see MarketingMobileMenu).
export function MarketingLayout({ children }: MarketingLayoutProps) {
  const t = useT();
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // A menu link tapped in-SPA navigates without unmounting the layout, so the
  // panel would otherwise stay open over the new page — close it on any route
  // change.
  useEffect(() => setMenuOpen(false), [pathname]);

  // While open, Escape or a tap outside the header dismisses the menu, matching
  // the Learn More dropdown's affordances.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && !navRef.current?.contains(target)) setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [menuOpen]);

  return (
    <div className="layout-wrapper marketing-layout">
      {/* First tab stop on the page, ahead of the header, so a keyboard user
          can bypass the nav. Focusing <main> directly (rather than letting the
          browser follow the fragment) keeps the hash out of the router's URL. */}
      <a
        href="#main-content"
        className="skip-link"
        data-gp-name="skip-to-main"
        onClick={(event) => {
          event.preventDefault();
          document.getElementById('main-content')?.focus();
        }}
      >
        {t('skip-link.main-content')}
      </a>
      <div className="content-column">
        <nav className="primary-header" ref={navRef}>
          <div className="primary-header-frame">
            <div className="primary-header-pc">
              <div className="primary-header-row">
                <Link to="/" className="brand">GOALPOST</Link>
                <div className="nav-links" data-gp-name="primary-nav">
                  <LearnMoreMenu />
                  <Link to="/demo">Demo</Link>
                  <Link to="/pricing">Pricing</Link>
                  {/* Cross-origin into the product app. The app decides
                      authenticated-vs-anonymous: the log-in link targets the
                      protected /dashboard route, which bounces anonymous
                      visitors through /login. The nav uses the marketing CTA
                      keys ("Log in", and the shorter "Sign up" so it doesn't
                      crowd the bar) — the on-page CTA buttons keep the fuller
                      "Sign up free". */}
                  <Link to="/dashboard">{t('marketing.cta.login')}</Link>
                  <Link to="/register">{t('marketing.cta.signup')}</Link>
                </div>
                <button
                  type="button"
                  className="mkt-nav-toggle"
                  aria-label={t('hamburger-menu.toggle-aria')}
                  aria-expanded={menuOpen}
                  aria-controls="mkt-mobile-menu"
                  data-gp-name="mobile-nav-toggle"
                  onClick={() => setMenuOpen((o) => !o)}
                >
                  <span className="mkt-nav-toggle-line" />
                  <span className="mkt-nav-toggle-line" />
                  <span className="mkt-nav-toggle-line" />
                </button>
              </div>
            </div>
          </div>
          {menuOpen && <MarketingMobileMenu onNavigate={() => setMenuOpen(false)} />}
        </nav>
        {/* Page segment for every `data-gp-name` path inside — see useRouteName. */}
        <main id="main-content" tabIndex={-1} className="mkt-main" data-gp-name={useRouteName()}>
          {children}
        </main>
      </div>
    </div>
  );
}
